import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, GetProp, Image, Select, Upload } from 'antd';
import { UploadFile, UploadProps } from 'antd/es/upload';
import {
	getApi,
	postAdminPanelMedicineSync,
	postMedicineCatalogCsvSync,
	putApi,
	type AdminPanelMedicineSyncPayload,
} from 'apis';
import AutoCompleteField from 'components/Global/AutoCompleteField';
import InputWithLabelAndSvg from 'components/Global/InputWithLabel';
import { FORMDATA, sanitizeString } from 'constants/Data';
import { getNames } from 'country-list';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { normalizeSaltComposition } from 'utils/helper';
import { toastText, checkPermission } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import AddInput from 'components/Global/AddInput';
import WarningTagsInput from 'components/Global/WarningTagsInput';
import type { DraftEditPrefetchedReferenceData, MedicineDetailsByIdsRow } from '../types';

type Category = {
	id: string;
	name: string;
	subCategories: {
		id: string;
		name: string;
		categoryId: string;
	}[];
};

/** Use absolute URLs as-is; otherwise prepend the S3 base. */
const resolveMedicineImageUrl = (url?: string | null): string | undefined => {
	const trimmed = url?.trim();
	if (!trimmed) return undefined;
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `${process.env.REACT_APP_S3_BASE_URL || ''}${trimmed}`;
};

/** Strip a public/presigned URL back to the S3 object key for persistence. */
const toS3ImageKey = (urlOrKey?: string | null): string => {
	const trimmed = urlOrKey?.trim() || '';
	if (!trimmed) return '';
	if (!/^https?:\/\//i.test(trimmed)) return trimmed;
	try {
		const { pathname } = new URL(trimmed);
		return decodeURIComponent(pathname.replace(/^\//, ''));
	} catch {
		return trimmed;
	}
};

const medicineImageFileName = (urlOrKey?: string | null): string => {
	const key = toS3ImageKey(urlOrKey);
	return key.split('/').pop() || 'image';
};

const STRENGTH_AUTO_UNITS = ['TABLET', 'CAPSULE'];
const isStrengthAutoUnit = (unit: unknown) =>
	typeof unit === 'string' && STRENGTH_AUTO_UNITS.includes(unit);
const deriveStrengthFromSalt = (salt: unknown): string =>
	(Array.isArray(salt) ? salt : [])
		.map((entry) => String((entry as { strength?: string })?.strength ?? '').trim())
		.filter(Boolean)
		.join('/');

type Props = {
	closeDrawerByAnimation: () => void;
	editSelectedMedicine: MedicineDetailsByIdsRow & { draftRequestId?: string };
	setEditSelectedMedicine: (v: any) => void;
	fetchMedicines?: () => void | Promise<void>;
	setOptionsState: (fn: any) => void;
	optionsState: Record<string, { label: string; value: string }[]>;
	prefetchedReferenceData: DraftEditPrefetchedReferenceData;
};

const DraftMedicineEditBody: FC<Props> = (props) => {
	// Inits
	const { addMedicineFields } = FORMDATA;
	const {
		closeDrawerByAnimation,
		editSelectedMedicine,
		setEditSelectedMedicine,
		fetchMedicines,
		setOptionsState,
		optionsState,
		prefetchedReferenceData,
	} = props;

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile
	);

	const isImageEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['edit'],
	});

	const isImageAddPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['add'],
	});

	const isImageViewPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['view'],
	});

	const isImageDeletePermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['delete'],
	});

	const draftMedicinesRequestRow = medicinesPermissions?.data?.permissions?.find(
		(p: { moduleName?: string }) => p.moduleName === 'DraftMedicinesRequest'
	);
	const hasDraftMedicinesRequestFull =
		checkPermission(medicinesPermissions?.data?.permissions, {
			permissionName: 'DraftMedicinesRequest',
			permission: ['all'],
		}) ||
		Boolean(
			draftMedicinesRequestRow?.edit &&
				(draftMedicinesRequestRow?.view ||
					draftMedicinesRequestRow?.viewAll ||
					draftMedicinesRequestRow?.all)
		);

	// Image fields shown only if user has view permission (draft-only role uses DraftMedicinesRequest.all)
	const canViewImages = isImageViewPermission || hasDraftMedicinesRequestFull;

	// Can delete images if user has edit or delete permission
	const canDeleteImages =
		isImageEditPermission || isImageDeletePermission || hasDraftMedicinesRequestFull;

	const isMedicineEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['edit'],
	});

	const isMedicineAddPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['add'],
	});

	// For edit mode: check edit permission, for add mode: check add permission
	const canManageMedicines = editSelectedMedicine
		? isMedicineEditPermission || hasDraftMedicinesRequestFull
		: isMedicineAddPermission || hasDraftMedicinesRequestFull;

	// If user has image view permission but not medicine permission, disable all non-image fields
	const shouldDisableNonImageFields = canViewImages && !canManageMedicines;

	type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
	const [form] = Form.useForm();
	const [loading, setLoading] = useState<boolean>();
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [categoryList, setCategoryList] = useState([{ label: '', value: '' }]);
	const [doseForms, setDoseForms] = useState<{ label: string; value: string }[]>([]);
	const [doseFormOptionsForCategory, setDoseFormOptionsForCategory] = useState<{ label: string; value: string }[]>([]);
	const [loadingDoseFormsByCategory, setLoadingDoseFormsByCategory] = useState(false);
	const [warningTags, setWarningTags] = useState<{ label: string; value: string }[]>([]);
	const [selectedCategoryId, setSelectedCategoryId] = useState('');
	const [wholeCategories, setWholeCategories] = useState<unknown[]>([{}]);
	const skipMappingFirstFetchRef = useRef(true);
	const subCategoryOptions = useMemo(() => {
		return (
			(wholeCategories as Category[])?.find(
				(item: any) => item?.id === selectedCategoryId
			)?.subCategories?.map((sub: any) => ({
				label: sub.subCategoryName,
				value: sub.id,
			})) || []
		);
	}, [wholeCategories, selectedCategoryId]);


	const countries = getNames().map((name) => {
		if (name === 'Korea, Republic of') {
			return { label: 'South Korea', value: 'South Korea' };
		}
		if (name === "Korea, Democratic People's Republic of") {
			return { label: 'North Korea', value: 'North Korea' };
		}
		return { label: name, value: name };
	});

	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const debounceDelay = 400;

	const syncStrengthFromSalt = (saltVal: unknown) => {
		if (!isStrengthAutoUnit(form.getFieldValue('unitOfMeasure'))) return;
		form.setFieldsValue({ medicineStrength: deriveStrengthFromSalt(saltVal) });
	};

	const toOptions = (values: unknown[]): { label: string; value: string }[] => {
		const unique = new Set<string>();
		for (const v of values) {
			if (typeof v === 'string' && v) unique.add(v);
		}
		return Array.from(unique).map((v) => ({ label: v, value: v }));
	};

	const editData = useMemo(() => {
		const m = editSelectedMedicine || {};
		const normalizedSaltComposition = normalizeSaltComposition(m.saltComposition);
		const saltArr =
			normalizedSaltComposition.length > 0
				? normalizedSaltComposition
				: [{ name: '', strength: '' }];
		return {
			...m,
			medicineSecondaryImageUrl: m.medicineSecondaryImageUrl || [],
			medicinePrimaryImageUrl: m.medicinePrimaryImageUrl || '',
			saltComposition: saltArr,
			medicineStrength:
				m.medicineStrength ||
				(isStrengthAutoUnit(m.unitOfMeasure)
					? deriveStrengthFromSalt(normalizedSaltComposition)
					: m.medicineStrength) ||
				'',
			usesTags: Array.isArray(m.usesTags) ? m.usesTags : [],
		};
	}, [editSelectedMedicine]);
	const initialImg: UploadFile[] = (editData?.medicineSecondaryImageUrl || []).map(
		(item: any, index: number) => ({
			uid: index.toString(),
			name: medicineImageFileName(item),
			status: 'done' as const,
			url: resolveMedicineImageUrl(item),
		})
	);
	const [fileList, setFileList] = useState<UploadFile[]>(initialImg);
	const [primaryFileList, setPrimaryFileList] = useState<UploadFile[]>(
		editData?.medicinePrimaryImageUrl
			? [
					{
						uid: '1',
						name: medicineImageFileName(editData.medicinePrimaryImageUrl),
						status: 'done' as const,
						url: resolveMedicineImageUrl(editData.medicinePrimaryImageUrl),
					},
			  ]
			: []
	);



	const handleSearch = useCallback(async (value: string, field: keyof typeof optionsState) => {

		const stripTags = (str: string) => str?.replace(/<\/?[^>]+(>|$)/g, '');

		let searchValue = stripTags(value?.trim() || "");
		if (field === "usesTags") {
			const parts = value.split(",");
			searchValue = stripTags(parts[parts.length - 1]?.trim() || "");
		}
		const sanitizedValue = stripTags(value?.trim() || '');

		// Only continue if value has at least 3 characters
		if (!searchValue || !sanitizedValue || sanitizedValue.length < 3 || field === 'shortDescription') {
			setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
			return;
		}


		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(async () => {
			if (!sanitizedValue) {
				setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
				return;
			}
			try {
				const query = {
					targetField: field,
					search: searchValue,
				};
				const res = await getApi('/medicine/get-all', query);
				let formattedOptions: { label: string; value: string }[] = [];
				if (field === 'saltComposition') {
					const salts = res?.data?.data?.flatMap((item: any) => item?.saltComposition || []);
					const names = salts.map((s: any) => s?.name);
					const filtered = (names as unknown[]).filter((n) =>
						typeof n === 'string' && n.toLowerCase().includes(sanitizedValue.toLowerCase())
					);
					formattedOptions = toOptions(filtered);
				} else if (field === 'usesTags') {
					const tags = res?.data?.data?.flatMap((item: any) =>
						Array.isArray(item?.usesTags) ? item.usesTags : []
					);
					formattedOptions = toOptions(tags as unknown[]);
				} else {
					const fieldValues = res?.data?.data?.map((item: any) => item[field]);
					formattedOptions = toOptions(fieldValues as unknown[]);
				}
				setOptionsState((prev: any) => ({ ...prev, [field]: formattedOptions }));
			} catch {
				setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
			}
		}, debounceDelay);
	}, [setOptionsState]);

	const medicineAlreadyFinalized = editSelectedMedicine?.isDraft === false;

	const onFinish = async (values: any) => {
		if (loading) return;
		if (!editSelectedMedicine) return;

		const useStandardMedicineUpdate = medicineAlreadyFinalized;
		const draftKeyRaw = process.env.REACT_APP_MEDICINE_DRAFT_UPDATE_KEY?.trim();
		if (!useStandardMedicineUpdate && !draftKeyRaw) {
			toast.error('Draft update key is not configured');
			return;
		}

		const { primaryImage, secondaryImage, ...restData } = values;
		setLoading(true);
		const formData = new FormData();

		const canManageImagesForSubmit =
			isImageEditPermission || isImageAddPermission || hasDraftMedicinesRequestFull;
		if (canManageImagesForSubmit) {
			let primaryImageUrl: File | null = null;
			let existingPrimaryImageUrl: string | null = null;

			if (primaryFileList && primaryFileList.length) {
				const file = primaryFileList[0];

				if ('originFileObj' in file && file.originFileObj instanceof File) {
					primaryImageUrl = file.originFileObj;
				} else if (file.url) {
					existingPrimaryImageUrl = toS3ImageKey(file.url || file.name);
				}
			}

			if (primaryImageUrl) {
				formData.append('medicinePrimaryImageUrl', primaryImageUrl);
			} else if (existingPrimaryImageUrl) {
				formData.append('existingMedicinePrimaryImageUrl', existingPrimaryImageUrl);
			}

			const existingSecondaryImageUrls: string[] = [];
			const newFiles: File[] = [];

			if (fileList) {
				fileList.forEach((file) => {
					if ('originFileObj' in file && file.originFileObj instanceof File) {
						newFiles.push(file.originFileObj);
					} else if (file.url) {
						const key = toS3ImageKey(file.url || file.name);
						if (key) existingSecondaryImageUrls.push(key);
					}
				});
			}

			newFiles.forEach((file) => {
				formData.append('medicineSecondaryImageUrl', file);
			});

			if (editSelectedMedicine && existingSecondaryImageUrls) {
				formData.append(
					'existingMedicineSecondaryImageUrls',
					JSON.stringify(existingSecondaryImageUrls)
				);
			}
		} else if (editSelectedMedicine) {
			const existingPrimaryKey = toS3ImageKey(editData?.medicinePrimaryImageUrl);
			if (existingPrimaryKey) {
				formData.append('existingMedicinePrimaryImageUrl', existingPrimaryKey);
			}
			if (editData?.medicineSecondaryImageUrl && editData.medicineSecondaryImageUrl.length > 0) {
				formData.append(
					'existingMedicineSecondaryImageUrls',
					JSON.stringify(
						editData.medicineSecondaryImageUrl
							.map((url: string) => toS3ImageKey(url))
							.filter(Boolean),
					)
				);
			}
		}

		if (typeof restData.usesTags === 'string') {
			restData.usesTags = restData.usesTags.split(',').map((tag: string) => tag.trim());
		}

		if (restData.itemSize === undefined || restData.itemSize === null) {
			restData.itemSize = '';
		}

		if (!restData.numberOfItems || String(restData.numberOfItems).trim() === '') {
			restData.numberOfItems = '1';
		}

		if (Array.isArray(restData.saltComposition)) {
			restData.saltComposition = restData.saltComposition.filter((item: unknown) => {
				if (typeof item === 'object' && item !== null) {
					const o = item as { name?: string; strength?: string };
					const name = o.name?.trim() || '';
					const strength = o.strength?.trim() || '';
					return name !== '' && strength !== '';
				}
				return false;
			});
		}

		const unwrapSelectValue = (v: unknown): string => {
			if (v == null) return '';
			if (typeof v === 'object' && v !== null && 'value' in v && 'label' in v) {
				return String((v as { value: string }).value ?? '');
			}
			return String(v);
		};
		const sanitizeField = (v: unknown) => sanitizeString(unwrapSelectValue(v));

		const weightPart = unwrapSelectValue(restData.weightage).trim();
		const uomPart = unwrapSelectValue(restData.unitOfMeasure).trim();
		const packSizeDisplay = [weightPart, uomPart].filter(Boolean).join(' ').trim();

		// `content` mirrors Salt Composition rows in the form (name + strength), after empty rows are filtered.
		const saltContent = (restData.saltComposition as { name: string; strength: string }[]) ?? [];

		const adminPanelPayload: AdminPanelMedicineSyncPayload = {
			masterMedicineId: editData.id,
			rxProduct: restData.rxRequired === true,
		};
		const hsn = sanitizeField(restData.hsnCode);
		if (hsn) adminPanelPayload.hsnCode = hsn;
		const manufacturer = sanitizeField(restData.manufacturer);
		if (manufacturer) adminPanelPayload.manufacturer = manufacturer;
		if (saltContent.length > 0) adminPanelPayload.content = saltContent;
		if (packSizeDisplay) adminPanelPayload.packSize = packSizeDisplay;
		const medicineName = sanitizeField(restData.medicineName);
		if (medicineName) adminPanelPayload.medicineName = medicineName;
		const medicineStrength = sanitizeField(restData.medicineStrength);
		if (medicineStrength) adminPanelPayload.medicineStrength = medicineStrength;
		const scheduleType = sanitizeField(restData.scheduleType);
		if (scheduleType) adminPanelPayload.scheduleType = scheduleType;

		Object.entries(restData).forEach(([key, value]) => {
			// Do not send null-like values; avoids persisting the literal string "null".
			if (value === undefined || value === null) return;

			if (Array.isArray(value)) {
				value.forEach((item, index) => {
					if (typeof item === 'object') {
						formData.append(`${key}[${index}]`, JSON.stringify(item));
					} else {
						formData.append(`${key}[${index}]`, item as string | Blob);
					}
				});
			} else if (
				typeof value === 'object' &&
				value !== null &&
				'value' in value &&
				'label' in value
			) {
				formData.append(key, sanitizeString((value as { value: string }).value));
			} else if (typeof value === 'string') {
				formData.append(key, sanitizeString(value));
			} else {
				formData.append(key, String(value));
			}
		});

		try {
			let res;
			if (useStandardMedicineUpdate) {
				res = await putApi(`/medicine/update/${editData.id}`, formData, true);
			} else {
				res = await putApi(`/medicine/update-draft/${editData.id}`, formData, true, {
					'x-medicine-draft-update-key': draftKeyRaw!,
				});
			}

			if (res?.data?.responseStatus !== 200) {
				throw new Error(res?.data?.message || 'Something went wrong.');
			}

			const syncRes = await postMedicineCatalogCsvSync();
			if (syncRes.status < 200 || syncRes.status >= 300) {
				throw new Error('Catalog sync failed.');
			}

			const adminRes = await postAdminPanelMedicineSync(adminPanelPayload);
			if (adminRes.status < 200 || adminRes.status >= 300) {
				throw new Error('Admin panel medicine sync failed.');
			}

			toast.success(res?.data?.message);
			await fetchMedicines?.();
			closeDrawerByAnimation();
		} catch (error: unknown) {
			const err = error as { response?: { data?: { message?: string } }; message?: string };
			toast.error(err?.response?.data?.message || err?.message || 'Something went wrong.');
		} finally {
			setLoading(false);
		}
	};

	const handleOnClick = () => {
		closeDrawerByAnimation();
		setEditSelectedMedicine(null);
	}

	const handlePreview = async (file: UploadFile) => {
		if (!canViewImages) {
			return; // Don't allow preview if user doesn't have view permission
		}
		
		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as FileType);
		}

		setPreviewImage(file.url || (file.preview as string));

		setPreviewOpen(true);
	};

	const handleChange = (
		{ file, fileList: newFileList }: { file: UploadFile; fileList: UploadFile[] },
		type: 'primary' | 'secondary'
	) => {
		if (file?.size) {
			const isLt1M = file.size / 1024 / 1024 < 1;
			if (!isLt1M) {
				toastText('Image must be smaller than 1MB!', 'error');
				return;
			}
		}

		if (type === 'primary') {
			setPrimaryFileList(newFileList);
		} else {
			setFileList(newFileList);
		}
	};

	const canUploadImages =
		isImageEditPermission || isImageAddPermission || hasDraftMedicinesRequestFull;
	
	const uploadButton = (
		<button 
			style={{ border: 0, background: 'none' }} 
			type="button"
			disabled={!canUploadImages}
		>
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>Upload</div>
		</button>
	);

	const getBase64 = (file: FileType): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});

	const propsUpload = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 3,
		multiple: true,
		fileList: fileList,
		beforeUpload: () => false,
		listType: 'picture-card',
		onPreview: canViewImages ? handlePreview : undefined,
		onRemove: canDeleteImages ? undefined : () => false,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: canDeleteImages,
		},
	};

	const propsUploadPrimary = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 1,
		multiple: true,
		fileList: primaryFileList,
		beforeUpload: () => {
			return false; // Prevent automatic upload
		},
		onPreview: canViewImages ? handlePreview : undefined,
		onRemove: canDeleteImages ? undefined : () => false,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: canDeleteImages,
		},
	};

	const fetchDoseFormsByCategory = useCallback(
		async (categoryId: string) => {
			if (!categoryId) {
				setDoseFormOptionsForCategory([]);
				return;
			}
			setLoadingDoseFormsByCategory(true);
			try {
				const res = await getApi(`/category-dose-form-mapping/get-by-category/${categoryId}`);
				const mapped = (res?.data?.data || []).map((d: { id: string; name: string }) => ({
					label: d.name,
					value: d.id,
				}));
				const currentDoseFormId = editData?.doseFormId as string | undefined;
				if (
					currentDoseFormId &&
					!mapped.some((o: { value: string }) => o.value === currentDoseFormId) &&
					doseForms.length
				) {
					const current = doseForms.find((df) => df.value === currentDoseFormId);
					if (current) mapped.push(current);
				}
				setDoseFormOptionsForCategory(mapped);
			} catch {
				setDoseFormOptionsForCategory(doseForms);
			} finally {
				setLoadingDoseFormsByCategory(false);
			}
		},
		[doseForms, editData?.doseFormId]
	);

	const getDropdownOptions = (fieldId: string, roleOptions?: any[]) => {
		switch (fieldId) {
			case "countryOfOrigin":
				return countries;
			case "categoryId":
				return categoryList;
			case "subCategoryId":
				return subCategoryOptions;
			case "doseFormId":
				return selectedCategoryId ? doseFormOptionsForCategory : [];
			default:
				return roleOptions?.map((opt: any) => ({
					label: opt.label,
					value: opt.value,
				})) || [];
		}
	};

	useEffect(() => {
		if (!prefetchedReferenceData) return;
		skipMappingFirstFetchRef.current = true;
		setWholeCategories(prefetchedReferenceData.wholeCategories as Category[]);
		setCategoryList(
			prefetchedReferenceData.categoryList.length > 0
				? prefetchedReferenceData.categoryList
				: [{ label: '', value: '' }]
		);
		setDoseForms(prefetchedReferenceData.doseForms);
		setWarningTags(prefetchedReferenceData.warningTags);
		setDoseFormOptionsForCategory(prefetchedReferenceData.doseFormOptionsForCategory);
		const cid = editSelectedMedicine?.categoryId;
		setSelectedCategoryId(typeof cid === 'string' ? cid : '');
	}, [prefetchedReferenceData, editSelectedMedicine]);

	useEffect(() => {
		if (!selectedCategoryId) {
			return;
		}
		const medCat = editSelectedMedicine?.categoryId;
		if (
			skipMappingFirstFetchRef.current &&
			medCat &&
			selectedCategoryId === medCat
		) {
			skipMappingFirstFetchRef.current = false;
			return;
		}
		fetchDoseFormsByCategory(selectedCategoryId);
	}, [
		selectedCategoryId,
		fetchDoseFormsByCategory,
		editSelectedMedicine?.categoryId,
	]);

	useEffect(() => {
		const existingData = {
			liver: editData?.Warnings?.liver,
			liverTagId: editData?.Warnings?.liverTagId,
			kidney: editData?.Warnings?.kidney,
			kidneyTagId: editData?.Warnings?.kidneyTagId,
			alcohol: editData?.Warnings?.alcohol,
			alcoholTagId: editData?.Warnings?.alcoholTagId,
			pregnancy: editData?.Warnings?.pregnancy,
			pregnancyTagId: editData?.Warnings?.pregnancyTagId,
			driving: editData?.Warnings?.driving,
			drivingTagId: editData?.Warnings?.drivingTagId,
			lactation: editData?.Warnings?.lactation,
			lactationTagId: editData?.Warnings?.lactationTagId,
		};

		form.setFieldsValue(existingData);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate warnings when medicine loads
	}, [editSelectedMedicine?.id]);

	// JSX
	return (
		<div className={styles['side-drawer-body']}>
			<Form
				form={form}
				name="basic"
				initialValues={{
					...editData,
					mrp: editData?.mrp != null ? String(editData.mrp) : undefined,
					weightage: editData?.weightage != null ? String(editData.weightage) : undefined,
					gstPercentage: editData?.gstPercentage != null ? String(editData.gstPercentage) : undefined,
					categoryId: editData?.categoryId,
					subCategoryId: editData?.subCategoryId,
					description: editData?.description,
					shortDescription:
						(editData?.shortDescription as string | undefined) ??
						(editData?.description as string | undefined) ??
						'',
					numberOfItems: editData?.numberOfItems ?? '1',
					usesTags: editData?.usesTags?.join(', ') || '',
				}}
				onFinish={onFinish}
				autoComplete="off"
				layout="vertical"
				labelAlign="left"
				className={styles['side-drawer-form']}
			>
				<div className={styles['side-drawer-form__inputs']}>
					{addMedicineFields.map(
						(singleField: any, index: number) => {
							// Skip image fields if user doesn't have Images view permission
							if ((singleField.type === 'primaryImage' || singleField.type === 'secondaryImage') && !canViewImages) {
								return null;
							}

							return (
								<React.Fragment key={index}>
									{singleField.type === 'text' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<AutoCompleteField
												form={form}
												singleUserInput={singleField}
												options={optionsState[singleField?.name as keyof typeof optionsState]}
												onSearch={handleSearch}
												disabled={shouldDisableNonImageFields}
											/>
										</div>
									)}

									{singleField.type === 'number' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<InputWithLabelAndSvg
												singleUserInput={singleField}
												disabled={shouldDisableNonImageFields}
											/>
										</div>
									)}

									{singleField.type === 'dropdown' && (
										<Form.Item
											key={singleField.id}
											name={singleField.name}
											label={singleField.title}
											rules={singleField.rules}
											className={`${styles["side-drawer-form__medicine--select"]} ${shouldDisableNonImageFields ? styles["side-drawer-form__medicine--select-disabled"] : ''}`}
										>
											<Select
												placeholder={singleField.id === 'doseFormId' && !selectedCategoryId ? 'Select category first' : singleField.placeHolder}
												options={getDropdownOptions(singleField.id, singleField.roleOptions)}
												showSearch
												optionFilterProp="label"
												style={{ height: "42px" }}
												disabled={shouldDisableNonImageFields}
												loading={singleField.id === 'doseFormId' ? loadingDoseFormsByCategory : false}
												onChange={(val: any) => {
													if (singleField.id === "categoryId") {
														setSelectedCategoryId(val || '');
														form.setFieldsValue({ subCategoryId: undefined, doseFormId: undefined });
														if (!val) {
															setDoseFormOptionsForCategory([]);
														}
													}
													if (singleField.id === 'unitOfMeasure') {
														if (isStrengthAutoUnit(val)) {
															form.setFieldsValue({
																medicineStrength: deriveStrengthFromSalt(form.getFieldValue('saltComposition')),
															});
														} else {
															form.setFieldsValue({ medicineStrength: '' });
														}
													}
												}}
											/>
										</Form.Item>
									)}

									{singleField.type === 'primaryImage' && (
										<Form.Item label="Primary Image" name={singleField.name}>
											<div>
												<div className='images-wrap'>
													<Upload
														{...propsUploadPrimary}
														listType="picture-card"
														fileList={primaryFileList}
														onChange={(info) => handleChange(info, 'primary')}
														itemRender={(originNode) => (
															<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
																{originNode}
															</div>
														)}

													>
														{primaryFileList.length >= 1 ? null : uploadButton}
													</Upload>
												</div>

												{/* Image preview */}
												{previewImage && (
													<Image
														wrapperStyle={{ display: 'none' }}
														preview={{
															visible: previewOpen,
															onVisibleChange: (visible) => setPreviewOpen(visible),
															afterOpenChange: (visible) => !visible && setPreviewImage(''),
														}}
														src={previewImage}
													/>
												)}
											</div>
										</Form.Item>
									)}

									{singleField.type === 'secondaryImage' && (
										<Form.Item label="Secondary Image" name={singleField.name}>
											<div>
												<div className='images-wrap'>
													<Upload
														{...propsUpload}
														listType="picture-card"
														fileList={fileList}
														onChange={(info) => handleChange(info, 'secondary')}
														itemRender={(originNode) => (
															<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
																{originNode}
															</div>
														)}

													>
														{fileList.length >= 3 ? null : uploadButton}
													</Upload>
												</div>

												{/* Image preview */}
												{previewImage && (
													<Image
														wrapperStyle={{ display: 'none' }}
														preview={{
															visible: previewOpen,
															onVisibleChange: (visible) => setPreviewOpen(visible),
															afterOpenChange: (visible) => !visible && setPreviewImage(''),
														}}
														src={previewImage}
													/>
												)}
											</div>
										</Form.Item>
									)}

									{singleField.type === 'addInput' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<Form.Item
												key={singleField.id}
												name={singleField.name}
												rules={singleField.rules}
											>
												<AddInput
													label={singleField.title}
													name1="name"
													name2="strength"
													placeholder1="Salt Name"
													placeholder2="Strength"
													required={singleField.required}
													value={form.getFieldValue(singleField?.name) || [{ name: '', strength: '' }]}
													onChange={(val) => {
														form.setFieldsValue({ [singleField.name]: val });
														if (singleField.name === 'saltComposition') {
															syncStrengthFromSalt(val);
														}
													}}
													options={singleField.name === 'saltComposition' ? optionsState.saltComposition : undefined}
													onSearch={singleField.name === 'saltComposition' ? (val) => handleSearch(val, 'saltComposition') : undefined}
													isError={false}
													disabled={shouldDisableNonImageFields}
												/>
											</Form.Item>
										</div>
									)}

									{singleField.type === 'warning' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<Form.Item noStyle>
												<WarningTagsInput
													label={singleField.title}
													name1={singleField.name1}
													name2={singleField.name2}
													placeholder1="Enter warning"
													placeholder2="Enter warning tag"
													rules={singleField.rules}
													warningTags={warningTags}
													disabled={shouldDisableNonImageFields}
												/>
											</Form.Item>
										</div>
									)}
								</React.Fragment>
							);
						}
					)}
				</div>

				{medicineAlreadyFinalized && (
					<p
						style={{ margin: '0 0 12px', fontSize: 13, color: 'rgba(0,0,0,0.55)' }}
					>
						This medicine is no longer a draft. Saving applies a standard medicine update (draft key not
						required; quality check returns to pending per policy).
					</p>
				)}

				<div className={styles['side-drawer-form__buttons']}>
					<Button
						htmlType="submit"
						disabled={loading}
						className={styles['side-drawer-form__buttons--edit']}
						loading={loading}
						title={
							medicineAlreadyFinalized
								? 'Standard medicine update — no draft finalize header'
								: 'Finalize draft (requires draft update key)'
						}
					>
						{medicineAlreadyFinalized ? 'Save changes' : editSelectedMedicine ? 'Update' : 'Save'}
					</Button>

					<Button
						htmlType="reset"
						className={styles['side-drawer-form__buttons--cancel']}
						onClick={handleOnClick}
					>
						Cancel
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default DraftMedicineEditBody;


